import { GameContext, UserContext } from "Components/Context";
import { useContext, useMemo } from "react";
import { Config } from "services/Config";
import { get, patch } from "services/Helper";

class UserService {
  static drawBucket = "default";
  static keyPrefix = "randostat:custom-draws";
  static preferencesKeyPrefix = "randostat:preferences";
  static preferenceSyncTimers = new Map();
  static preferenceSyncPayloads = new Map();
  static defaultPreferenceSyncDelayMs = 1500;

  static getUserId = (user) => user?.id ?? user?.user_id ?? null;

  static getStorageKey = (user) => {
    const userId = this.getUserId(user) ?? "anonymous";
    return `${this.keyPrefix}:${userId}`;
  };

  static getPreferencesStorageKey = (user) => {
    const userId = this.getUserId(user) ?? "anonymous";
    return `${this.preferencesKeyPrefix}:${userId}`;
  };

  static readStore = (user) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return {};
    }
    try {
      const raw = window.localStorage.getItem(this.getStorageKey(user));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (err) {
      return {};
    }
  };

  static writeStore = (user, store) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(this.getStorageKey(user), JSON.stringify(store));
    } catch (err) {
      // no-op: keep app behavior stable when storage is unavailable/quota exceeded
    }
  };

  static readCachedPreferences = (user) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return {};
    }
    try {
      const raw = window.localStorage.getItem(this.getPreferencesStorageKey(user));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (err) {
      return {};
    }
  };

  static writeCachedPreferences = (user, preferences) => {
    if (typeof window === "undefined" || !window.localStorage) {
      return;
    }
    try {
      window.localStorage.setItem(
        this.getPreferencesStorageKey(user),
        JSON.stringify(preferences && typeof preferences === "object" ? preferences : {})
      );
    } catch (err) {
      // no-op: keep app behavior stable when storage is unavailable/quota exceeded
    }
  };

  static isObject = (value) =>
    Boolean(value) && typeof value === "object" && !Array.isArray(value);

  static isEqual = (left, right) => {
    if (left === right) {
      return true;
    }

    if (this.isObject(left) && this.isObject(right)) {
      const leftKeys = Object.keys(left);
      const rightKeys = Object.keys(right);
      if (leftKeys.length !== rightKeys.length) {
        return false;
      }

      return leftKeys.every((key) => this.isEqual(left[key], right[key]));
    }

    if (Array.isArray(left) && Array.isArray(right)) {
      if (left.length !== right.length) {
        return false;
      }
      return left.every((item, index) => this.isEqual(item, right[index]));
    }

    return false;
  };

  static getChangedPreferences = (base, patchValue) => {
    const safeBase = this.isObject(base) ? base : {};
    const safePatch = this.isObject(patchValue) ? patchValue : {};
    const changed = {};

    Object.entries(safePatch).forEach(([key, nextValue]) => {
      const previousValue = safeBase[key];

      if (this.isObject(nextValue) && this.isObject(previousValue)) {
        const nestedChanged = this.getChangedPreferences(previousValue, nextValue);
        if (Object.keys(nestedChanged).length > 0) {
          changed[key] = nestedChanged;
        }
        return;
      }

      if (!this.isEqual(previousValue, nextValue)) {
        changed[key] = nextValue;
      }
    });

    return changed;
  };

  static mergePreferences = (base, patchValue) => {
    const safeBase = this.isObject(base) ? base : {};
    const safePatch = this.isObject(patchValue) ? patchValue : {};
    const result = { ...safeBase };

    Object.entries(safePatch).forEach(([key, value]) => {
      if (this.isObject(value) && this.isObject(result[key])) {
        result[key] = this.mergePreferences(result[key], value);
        return;
      }
      result[key] = value;
    });

    return result;
  };

  static getResolvedPreferences = (user, remotePreferences, fallbackPreferences = {}) => {
    const resolvedPreferences = this.mergePreferences(fallbackPreferences, remotePreferences);
    this.writeCachedPreferences(user, resolvedPreferences);
    return resolvedPreferences;
  };

  static schedulePreferencesSync = (user, partialPreferences, delayMs = this.defaultPreferenceSyncDelayMs) => {
    const userId = this.getUserId(user);
    if (!userId) {
      return;
    }

    const pendingPayload = this.mergePreferences(
      this.preferenceSyncPayloads.get(userId),
      partialPreferences
    );
    this.preferenceSyncPayloads.set(userId, pendingPayload);

    const existingTimer = this.preferenceSyncTimers.get(userId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      const payload = this.preferenceSyncPayloads.get(userId) || {};
      if (Object.keys(payload).length > 0) {
        this.setUserPreferences(user, payload).catch((error) => {
          console.error("Failed to sync user preferences:", error);
        });
      }
      this.preferenceSyncTimers.delete(userId);
      this.preferenceSyncPayloads.delete(userId);
    }, delayMs);

    this.preferenceSyncTimers.set(userId, timer);
  };

  static persistUserPreferences = (user, partialPreferences, delayMs = this.defaultPreferenceSyncDelayMs) => {
    if (!this.isObject(partialPreferences)) {
      return {};
    }

    const currentPreferences = this.readCachedPreferences(user);
    const changedPreferences = this.getChangedPreferences(currentPreferences, partialPreferences);
    if (Object.keys(changedPreferences).length === 0) {
      return currentPreferences;
    }

    const nextPreferences = this.mergePreferences(currentPreferences, changedPreferences);

    this.writeCachedPreferences(user, nextPreferences);
    this.schedulePreferencesSync(user, changedPreferences, delayMs);

    return nextPreferences;
  };

  static saveCustomDraws = (user, gameName, draws, drawName = this.drawBucket) => {
    if (!user || !gameName || !Array.isArray(draws)) return;
    const store = this.readStore(user);
    if (!store[drawName] || typeof store[drawName] !== "object") {
      store[drawName] = {};
    }
    store[drawName][gameName] = draws;
    this.writeStore(user, store);
  };

  static getCustomDraws = (user, gameName, drawName = this.drawBucket) => {
    if (!user || !gameName) return undefined;
    const store = this.readStore(user);
    const bucket = store?.[drawName];
    if (!bucket || typeof bucket !== "object" || 
      !bucket[gameName] || bucket[gameName].length === 0
    ) {
      return undefined;
    }
    return bucket[gameName];
  };

  static getUser = () => {
    return get(Config.getUsersUrl());
  }

  static setUserPreferences = (user, preferences) => {
    if (!user || !this.getUserId(user) || typeof preferences !== "object") {
      return Promise.reject(new Error("Invalid user or preferences"));
    }
    return patch(`${Config.getUsersUrl()}`, preferences);
  }
}

const useUserService = (gameNameOverride) => {
  const { user } = useContext(UserContext);
  const { gameRules } = useContext(GameContext);
  const gameName = gameNameOverride || gameRules?.id || gameRules?.backendKey || gameRules?.name;
  return useMemo(() => ({
    saveCustomDraws: (draws, drawName) => UserService.saveCustomDraws(user, gameName, draws, drawName),
    getCustomDraws: (drawName) => UserService.getCustomDraws(user, gameName, drawName),
  }), [user, gameName]);
};

export { useUserService, UserService }
